import { OnboardingDraft } from '../interfaces/onboarding-interfaces';

// The local half of the onboarding safety net.
//
// Two copies of a draft exist and neither is the cache of the other:
//   - THIS one, in localStorage, written synchronously on every keystroke. It is
//     the copy that survives a lost connection, a crashed tab and a closed
//     laptop, because writing it needs no network.
//   - The server copy on users.onboarding_state, written debounced. It is the
//     copy that survives a different browser, a different device, and cleared
//     site data.
// They are reconciled by `updated_at`, newest wins. See use-onboarding-draft.ts.

const STORAGE_PREFIX = 'lobbero.onboarding.drafts';

type DraftMap = Record<string, OnboardingDraft>;

// Namespaced per user: a shared browser must never hand one owner's half-typed
// portfolio names to the next account that signs in on it.
const storageKey = (userId: string | null): string =>
  `${STORAGE_PREFIX}.${userId || 'anon'}`;

// Every localStorage call here is guarded. Safari in private mode throws on
// setItem once the (zero) quota is hit, and losing a draft is not a reason to
// take the onboarding screen down with it.
const readMap = (userId: string | null): DraftMap => {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));

    if (!raw) {
      return {};
    }

    const parsed: unknown = JSON.parse(raw);

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    return parsed as DraftMap;
  } catch {
    return {};
  }
};

const writeMap = (userId: string | null, map: DraftMap): void => {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(map));
  } catch {
    // Quota or a disabled store. The server copy is still attempted.
  }
};

export const readLocalDraft = (
  userId: string | null,
  step: string
): OnboardingDraft | null => {
  const entry = readMap(userId)[step];

  if (!entry || typeof entry !== 'object' || !entry.data) {
    return null;
  }

  return entry;
};

export const writeLocalDraft = (
  userId: string | null,
  step: string,
  data: Record<string, unknown>
): string => {
  const updatedAt = new Date().toISOString();
  const map = readMap(userId);

  map[step] = { data, updated_at: updatedAt };
  writeMap(userId, map);

  return updatedAt;
};

export const clearLocalDraft = (userId: string | null, step: string): void => {
  const map = readMap(userId);

  delete map[step];
  writeMap(userId, map);
};

// Called once onboarding is finished. Removes the whole namespace rather than
// each step, so a flow that gained a step since the draft was written does not
// leave an orphan behind.
export const clearAllLocalDrafts = (userId: string | null): void => {
  try {
    window.localStorage.removeItem(storageKey(userId));
  } catch {
    // See writeMap.
  }
};

// Newest wins. A missing or unparseable timestamp loses to a real one, and two
// drafts with no timestamps at all resolve to the local copy — it is the one
// that was necessarily written last, since the server copy is only ever a
// debounced echo of it.
export const pickFresherDraft = (
  local: OnboardingDraft | null,
  server: OnboardingDraft | null | undefined
): OnboardingDraft | null => {
  if (!local) {
    return server || null;
  }

  if (!server) {
    return local;
  }

  const localTime = Date.parse(local.updated_at || '');
  const serverTime = Date.parse(server.updated_at || '');

  if (Number.isNaN(serverTime)) {
    return local;
  }

  if (Number.isNaN(localTime)) {
    return server;
  }

  return serverTime > localTime ? server : local;
};
